import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useMutation } from '@tanstack/react-query'
import { Loader2, AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { exchangeAppCode } from '@/api/auth'
import { useAuthStore } from '@/stores/useAuthStore'
import { defaultLocale, isLocale } from '@/lib/locales'
import { Button } from '@/components/ui/button'
import { STORAGE_KEY as MOBILE_BROWSER_ACK_KEY } from '@/components/MobileBrowserSplash'

/**
 * Landing of the iPhone app's "More settings on the web" link (My receptionist):
 * exchanges the one-time code for tokens, then opens the AI agent's page.
 * Never the plans or a purchase (Apple's rule). A risky sign-in still goes
 * through the code screen: AuthGuard sends there and comes back.
 */
export function AppliCallbackPage() {
  const { t } = useTranslation()
  const { locale } = useParams<{ locale: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const setTokens = useAuthStore((s) => s.setTokens)
  const clearAuth = useAuthStore((s) => s.clear)
  const startedRef = useRef(false)
  const [failed, setFailed] = useState(false)

  const effectiveLocale = isLocale(locale) ? locale : defaultLocale

  const mutation = useMutation({
    mutationFn: async (code: string) => {
      const data = await exchangeAppCode(code)
      clearAuth()
      setTokens(data.access, data.refresh)
      // 'agenda': the agent's calendar section (Square or Wix settings), already open.
      const section = data.page === 'agenda' ? '&agentTab=actions&capacite=calendrier' : ''
      const target = data.agent_id
        ? `/${effectiveLocale}/manager?view=ai-agents&edit=${data.agent_id}${section}`
        : `/${effectiveLocale}/manager?view=ai-agents`
      // Coming from the app is a deliberate choice of the web: no "mobile browser is
      // limited" screen, and the desktop layout as after "Continue in the browser",
      // which main.tsx applies on a full load (tokens are already in localStorage).
      try {
        localStorage.setItem(MOBILE_BROWSER_ACK_KEY, '1')
      } catch {
        // Private browsing: the screen still offers "Continue in the browser".
      }
      window.location.replace(target)
    },
    onError: (err) => {
      console.error('[AppliCallback]', err)
      setFailed(true)
    },
  })

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true
    const code = searchParams.get('code')
    if (!code) {
      setFailed(true)
      return
    }
    mutation.mutate(code)
  }, [searchParams, mutation])

  if (failed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A192F] text-white px-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-amber-400" />
          <h2 className="text-xl font-semibold">{t('appliCallback.errorTitle')}</h2>
          <p className="text-white/80 mt-2">{t('appliCallback.invalidLink')}</p>
          <Button
            type="button"
            onClick={() => navigate(`/${effectiveLocale}/login`)}
            className="mt-6 bg-white text-[#0A192F] hover:bg-white/90"
          >
            {t('appliCallback.backToLogin')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A192F] text-white">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
        <h2 className="text-xl font-semibold">{t('appliCallback.connecting')}</h2>
      </div>
    </div>
  )
}
